import { forwardRef, useCallback } from "react";
import type { ComponentPropsWithRef, MouseEventHandler } from "react";
import { Link, Section } from "@react-email/components";
import { omit } from "lodash";
import type { StyleValue } from "@/react/components/shared/BuilderUI/StyleValue.types";
import type { BlockComponent } from "@circle-react-shared/BuilderUI/BuilderUI.types";
import { useParseStyles } from "../../../Design/useParseStyles";
import { defaultStyles } from "./defaultStyles";

interface LinkBlockProps extends ComponentPropsWithRef<typeof Section> {
  "data-preview-mode": boolean;
  href: string;
  styles: Record<string, StyleValue>;
  text: string;
}

export const LinkBlock: BlockComponent = forwardRef<
  HTMLTableElement,
  LinkBlockProps
>(
  (
    { text, href, "data-preview-mode": isPreviewMode, styles, ...rest },
    ref,
  ) => {
    const {
      margin: marginStyles,
      padding: paddingStyles,
      sizing: sizingStyles,
      textAlign: textAlignStyles,
      ...restStyles
    } = styles;
    const sectionStyles = useParseStyles({
      styles: {
        margin: marginStyles,
        padding: paddingStyles,
        sizing: sizingStyles,
        textAlign: textAlignStyles,
      },
      defaultStyles: {
        margin: defaultStyles.margin,
        padding: defaultStyles.padding,
      },
    });
    const inlineStyles = useParseStyles({
      styles: restStyles,
      defaultStyles: omit(defaultStyles, ["margin", "padding"]),
    });

    const handleClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
      event => {
        if (isPreviewMode) event.preventDefault();
      },
      [isPreviewMode],
    );

    return (
      <Section ref={ref} style={sectionStyles} {...rest}>
        <Link
          href={href}
          target="_blank"
          onClick={handleClick}
          rel="noopener"
          style={inlineStyles}
        >
          {text}
        </Link>
      </Section>
    );
  },
);

LinkBlock.displayName = "LinkBlock";
